import { observer } from 'mobx-react';
import { Column, Pie } from '@ant-design/plots';
import { Card } from 'antd';

import {
    useApplicationSelectionStore,
    useOnboardingStore,
} from '~contexts/mobx';
import { OnboardingSection } from '~onboarding-components/onboarding-section/index';
import { BLUE_800, ERROR_800 } from '~styles/colors';
import style from './style.module.scss';
import {
    buildColumnChartConfig,
    buildPieChartConfigs,
    buildSegmentationData,
} from './helper';

const Dot = ({ color }: { color: string }) => (
    <span className={style.legend_dot} style={{ background: color }} />
);

const LegendPill = ({ color, label }: { color: string; label: string }) => (
    <div className={style.period_legend_pill}>
        <Dot color={color} />
        <span>{label}</span>
    </div>
);

export const SegmentationSurveyChartSection = observer(
    ({ isComparing }: { isComparing: boolean }) => {
        const onboardingStore = useOnboardingStore();
        const applicationSelectionStore = useApplicationSelectionStore();
        const appTarget = applicationSelectionStore.appTarget;

        const chartData = buildSegmentationData(
            onboardingStore.segmentationData,
            onboardingStore.segmentationDataPrevious,
            isComparing,
            appTarget
        );

        const columnChartConfig = buildColumnChartConfig(
            chartData,
            isComparing
        );

        const pieConfigs = buildPieChartConfigs(chartData, isComparing);
        const { labels, currentColors, outerPieConfig } = pieConfigs;

        return (
            <OnboardingSection
                title="Segmentation Survey"
                isLoading={onboardingStore.isFetchingSegmentationData}
            >
                <section className={style.card_wrapper}>
                    <Card className={style.full_width}>
                        <div className={style.flex}>
                            <div className={style.bar_chart_container}>
                                <Column
                                    {...columnChartConfig}
                                    key={`bar-${isComparing}`}
                                />
                            </div>

                            <div className={style.donut_chart_container}>
                                {pieConfigs.isComparing && (
                                    <div className={style.period_legend_row}>
                                        <LegendPill
                                            color={ERROR_800}
                                            label="Previous period"
                                        />
                                        <LegendPill
                                            color={BLUE_800}
                                            label="Current period"
                                        />
                                    </div>
                                )}

                                <div className={style.donut_chart_box}>
                                    <div className={style.donut_stack}>
                                        <div
                                            className={style.outer_donut_chart}
                                        >
                                            <Pie
                                                {...outerPieConfig}
                                                key={`outer-${pieConfigs.isComparing}`}
                                            />
                                        </div>

                                        {pieConfigs.showInnerPie && (
                                            <div
                                                className={
                                                    style.inner_donut_chart
                                                }
                                            >
                                                <Pie
                                                    {...pieConfigs.innerPieConfig}
                                                    key={`inner-${pieConfigs.isComparing}`}
                                                />
                                            </div>
                                        )}
                                    </div>

                                    <div
                                        className={
                                            style.segment_legend_container
                                        }
                                    >
                                        {labels.map((name, i) => (
                                            <div
                                                key={name}
                                                className={
                                                    style.segment_legend_item
                                                }
                                            >
                                                <div
                                                    className={
                                                        style.segment_legend_marker
                                                    }
                                                >
                                                    {pieConfigs.isComparing && (
                                                        <Dot
                                                            color={
                                                                pieConfigs
                                                                    .previousColors[
                                                                    i
                                                                ]
                                                            }
                                                        />
                                                    )}
                                                    <Dot
                                                        color={currentColors[i]}
                                                    />
                                                </div>

                                                <span
                                                    className={
                                                        style.segment_legend_label
                                                    }
                                                >
                                                    {name}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Card>
                </section>
            </OnboardingSection>
        );
    }
);
